import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { AUTHORIZATION } from '../constants/headers';
import eventBus from '../helpers/eventBus';
import EventNames from '../models/eventNames';
import { captureException, captureMessage } from '../plugins/sentry';
import TokenService from './token';
import { isTokenRefreshError } from './tokenRefreshError';
import { isWebView } from './web-view-service';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * One-shot guard for the session-recovery replays below: a replayed request
     * must never trigger another recovery attempt, so two consecutive failures
     * fall through to the normal logout handling instead of looping.
     */
    sessionRecoveryAttempted?: boolean;
    tokenRefreshAttempted?: boolean;
  }
}

const reportUnauthorizedAfterRefresh = (error: Pick<AxiosError, 'config' | 'response'>): void => {
  captureMessage({
    message: 'Request is still unauthorized after a JWT refresh',
    extra: {
      request: { url: error.config?.url, method: error.config?.method },
      responseStatus: error.response?.status,
    },
  });
};

export const shouldRefreshAndReplay = (
  config: AxiosRequestConfig,
  error: Pick<AxiosError, 'config' | 'response'>,
): boolean => {
  if (config.tokenRefreshAttempted === true) {
    reportUnauthorizedAfterRefresh(error);

    return false;
  }

  config.tokenRefreshAttempted = true;

  return true;
};

export const isMissingTokenResponse = (data: unknown): boolean =>
  typeof data === 'object'
  && data !== null
  && 'status' in data
  && (data as { status?: unknown }).status === 'missing-token';

export const canRecoverMissingToken = (config: AxiosRequestConfig | undefined): config is AxiosRequestConfig =>
  config !== undefined
  && config.sessionRecoveryAttempted !== true
  && TokenService.hasRefreshToken();

export const recoverMissingToken = async (instance: AxiosInstance, config: AxiosRequestConfig): Promise<AxiosResponse<unknown>> => {
  config.sessionRecoveryAttempted = true;
  await TokenService.requestNewToken();

  // The request interceptor injects the refreshed Bearer token from the cookie.
  return instance.request(config);
};

/**
 * A 401 from an internal Yii endpoint (relative URL) usually means the PHP
 * session is gone. For webview users the server-side SilentSessionRestoreService
 * can rebuild it from the JWT cookies on the next request, so the request is
 * worth replaying once before treating the 401 as a logout.
 */
export const canRetryInternalUnauthorized = (config: AxiosRequestConfig | undefined): config is AxiosRequestConfig =>
  config !== undefined
  && config.sessionRecoveryAttempted !== true
  && isWebView()
  && (TokenService.hasToken() || TokenService.hasRefreshToken());

export const retryInternalRequest = (instance: AxiosInstance, config: AxiosRequestConfig): Promise<AxiosResponse<unknown>> => {
  config.sessionRecoveryAttempted = true;

  return instance.request(config);
};

/**
 * Decides whether an unauthorized response is a replayable internal (relative-URL)
 * request and, if so, replays it once. Returns the replay promise, or `null` when
 * the caller should fall through to its normal logout handling. Centralizes the
 * URL guards so every axios factory shares one definition instead of duplicating it.
 */
export const replayInternalUnauthorized = (
  instance: AxiosInstance | null,
  config: AxiosRequestConfig | undefined,
  requestUrl: string,
): Promise<AxiosResponse<unknown>> | null => {
  if (
    requestUrl.indexOf('/jwt/refresh') === -1
    && requestUrl.indexOf('/') === 0
    && instance !== null
    && canRetryInternalUnauthorized(config)
  ) {
    // Replay the internal request once so SilentSessionRestoreService can
    // rebuild the PHP session server-side. If the replay fails again, the
    // interceptor runs on it with the one-shot guard set and logs out.
    return retryInternalRequest(instance, config);
  }

  return null;
};

export interface UnauthorizedContext {
  instance: AxiosInstance;
  config: InternalAxiosRequestConfig;
  requestUrl: string;
  error: AxiosError;
}

/**
 * Shared handler for the `error/isUnauthorized` branch of the theme axios
 * factories (paybis + kano `vue/services/api.ts`), whose bodies were byte-identical.
 * External URLs refresh the JWT and replay; internal (relative) URLs replay once so
 * the server can restore the session; otherwise it reports and emits SESSION_TIMEOUT.
 * Resolves the recovered request, or rejects with the original error.
 */
export const handleUnauthorizedError = (
  { instance, config, requestUrl, error }: UnauthorizedContext,
): Promise<AxiosResponse<unknown>> => {
  const extra: Record<string, unknown> = {
    cookieEnabled: navigator.cookieEnabled,
    errorExtraMessage: 'Error in refresh method for token',
  };

  if (error.config) {
    extra.request = { url: error.config.url, method: error.config.method };
  }
  if (error.response) {
    extra.responseStatus = error.response.status;
  }

  if (requestUrl.indexOf('/jwt/refresh') === -1 && requestUrl.indexOf('/') !== 0) {
    if (!shouldRefreshAndReplay(config, error)) {
      return Promise.reject(error);
    }

    return TokenService.requestNewToken(requestUrl)
      .then(({ jwt }) => {
        config.headers[AUTHORIZATION] = `Bearer ${jwt}`;

        return instance.request(config);
      })
      .catch((refreshError: unknown) => {
        if (!isTokenRefreshError(refreshError)) {
          captureException({ error: refreshError, extra });
        }

        return Promise.reject(error);
      });
  }

  const internalReplay = replayInternalUnauthorized(instance, config, requestUrl);
  if (internalReplay !== null) {
    return internalReplay;
  }

  captureException({ error, extra });
  eventBus.emit(EventNames.SESSION_TIMEOUT);

  // Thrown (not rejected) so the caller's `return handleUnauthorizedError(...)`
  // propagates synchronously, matching the interceptors' original `throw error`.
  throw error;
};
